import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

/**
 * Marketing site router. Five public routes:
 *   - `/`             — the existing LandingPage (long-form homepage)
 *   - `/pricing`      — dedicated pricing detail (hero + 3-tier table + FAQ + CTA)
 *   - `/security`     — security + compliance posture
 *   - `/customers`    — customer stories + case-study tiles
 *   - `/changelog`    — release notes timeline
 *
 * All routes mount under the same root layout (currently passthrough — the
 * Nav + Footer live inside each page so the homepage's intentional
 * full-bleed treatment isn't broken). Code-split with `lazy()` so the
 * homepage's first-paint bundle stays lean; route chunks load on
 * navigation.
 */

const LandingPage = lazy(() => import('@/pages/landing').then((m) => ({ default: m.LandingPage })));
const PricingPage = lazy(() => import('@/pages/pricing').then((m) => ({ default: m.PricingPage })));
const SecurityPage = lazy(() =>
  import('@/pages/security').then((m) => ({ default: m.SecurityPage })),
);
const CustomersPage = lazy(() =>
  import('@/pages/customers').then((m) => ({ default: m.CustomersPage })),
);
const ChangelogPage = lazy(() =>
  import('@/pages/changelog').then((m) => ({ default: m.ChangelogPage })),
);

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div
          aria-busy="true"
          aria-label="Loading"
          className="min-h-[60vh] bg-[var(--color-canvas)]"
        />
      }
    >
      {children}
    </Suspense>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <PageSuspense>
      <Outlet />
    </PageSuspense>
  ),
  notFoundComponent: NotFoundPage,
});

/**
 * 404 handler — keeps the chrome consistent (Nav + Footer wrap a clear
 * message + "back to home" link) so an unknown URL never lands the user
 * on a blank or runtime-error screen. The portal app sets a similar
 * `defaultNotFoundComponent` on its router; marketing matches the pattern.
 */
function NotFoundPage() {
  return (
    <>
      <div className="bg-field" aria-hidden />
      <div className="relative z-10 flex min-h-[60vh] flex-col items-center justify-center gap-3 px-[var(--pad-x)] py-24 text-center">
        <span className="eyebrow">404</span>
        <h1 className="m-0 text-[clamp(28px,4vw,48px)] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--color-ink)]">
          Page not found.
        </h1>
        <p className="max-w-[44ch] text-[15px] leading-[1.55] text-[var(--color-ink-secondary)]">
          The page you’re looking for moved, was renamed, or never existed.
        </p>
        <a
          href="/"
          className="mt-4 inline-flex items-center rounded-md border border-[var(--color-accent)] bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-[var(--color-canvas)] transition-colors hover:border-[var(--color-accent-bright)] hover:bg-[var(--color-accent-bright)]"
        >
          Back to home →
        </a>
      </div>
    </>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: SecurityPage,
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersPage,
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/changelog',
  component: ChangelogPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  pricingRoute,
  securityRoute,
  customersRoute,
  changelogRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function MarketingRouter() {
  return <RouterProvider router={router} />;
}
